
import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getAnimationDelay } from "@/lib/utils";
import { ScheduleDay } from './ScheduleDay';
import { day1Data } from './data/day1';
import { day2Data } from './data/day2';

// Helper function to convert schedule items to the format expected by ScheduleDay
const convertToSessionFormat = (items: any[], day: string) => {
  return items.map(item => ({
    time: `${item.start_time.substring(0, 5)}-${item.end_time.substring(0, 5)}`,
    type: item.type || 'Session',
    title: item.title,
    speaker: item.speaker?.name || '',
    description: item.description || null,
    day: day  // Pass the day ID for routing
  }));
};

export const DayTabs = () => {
  const [activeTab, setActiveTab] = useState("day1");
  
  const day1Sessions = convertToSessionFormat(day1Data, "day1");
  const day2Sessions = convertToSessionFormat(day2Data, "day2");
  
  // Check URL hash on mount and when it changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'day1' || hash === 'day2') {
        setActiveTab(hash);
      }
    };
    
    // Listen for custom event from Schedule page
    const handleSetActiveTab = (event: CustomEvent) => {
      setActiveTab(event.detail);
    };
    
    // Check hash on initial load
    handleHashChange();
    
    // Add event listeners
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('setActiveTab', handleSetActiveTab as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('setActiveTab', handleSetActiveTab as EventListener);
    };
  }, []);
  
  return (
    <Tabs 
      value={activeTab} 
      className="w-full" 
      onValueChange={(value) => {
        if (value === 'day2') {
          // Navigate to the specific URL for day 2
          window.location.href = 'https://aiconference.london/agenda#day2';
          return;
        }
        setActiveTab(value);
        window.history.pushState(null, '', `#${value}`);
      }}
    >
      <TabsList className="grid w-full grid-cols-2 mb-12">
        <TabsTrigger 
          value="day1" 
          className="text-white bg-green-600 data-[state=active]:bg-green-600 data-[state=active]:text-white py-2.5 text-base font-medium"
        >
          November 25th
        </TabsTrigger>
        <a
          href="https://aiconference.london/agenda#day2"
          className="text-white bg-blue-600 data-[state=active]:bg-blue-700 data-[state=active]:text-white py-2.5 text-base font-medium flex items-center justify-center hover:bg-blue-700 transition-colors"
        >
          November 26th
        </a>
      </TabsList>
      
      <div
        className="animate-fade-in"
        style={{ animationDelay: getAnimationDelay(1, 200) }}
      >
        <TabsContent value="day1" className="space-y-4 mt-0">
          <ScheduleDay sessions={day1Sessions} title="Day 1 Schedule" />
        </TabsContent>
        
        <TabsContent value="day2" className="space-y-4 mt-0">
          <ScheduleDay sessions={day2Sessions} title="Day 2 Schedule" />
        </TabsContent>
      </div>
    </Tabs>
  );
};

export default DayTabs;
